import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { I18nextProvider } from 'react-i18next';

import i18n from '../../i18n';
import App from '../../App';
import { generateUUID, loadScript, loadStyleSheet } from '../../util/util';
import { MessageHelper } from '../../util/message-helper';
import useReelsRoute from './useReelsRoute';
import { readReelsUrl, reelsShareTemplate } from './reels-url';

import { PlayerProvider } from '../../contexts/PlayerContext';
import { ConfigurationProvider } from '../../contexts/ConfigurationContext';
import { ChatLoginProvider } from '../../contexts/ChatLoginContext';
import { PresenterProvider } from '../../contexts/PresenterContext';
import { ShoppingProvider } from '../../contexts/ShoppingContext';
import { RestrictionProvider } from '../../contexts/RestrictionContext';

const StrictMode = process.env.NODE_ENV === 'development' ? React.StrictMode : React.Fragment;

// Locks the page while the feed is over it. <html> as well as <body>, or a document wider than the
// screen lays the fixed overlay out against the document instead of the viewport.
const REELS_BODY_CLASS = 'qplayer-reels-open';
// The portal's own full-screen element. <qplayer-reels-embed> cannot serve as the container: it
// neither holds the player DOM nor has a size once the feed is portalled out of it.
const REELS_HOST_CLASS = 'qplayer-reels-host';

// Feeds currently over the page. Module-level: the lock belongs to the document, not to an element.
let openFeedCount = 0;

/**
 * <qplayer-reels-embed> - the element a publisher puts on a gallery page.
 *
 * Three ways in: a card click, an address matching data-qplayer-reels-url, or openReels(). Per-site
 * config arrives through the dataset; only playlistId, mediaId and trigger vary, so they are state.
 */
const QplayerAppReels = (props) => {
	const { container } = props;

	// The whole of this component's state: the open request, or null for closed.
	const [openRequest, setOpenRequest] = useState(null);

	// QplayerApp's asset gate, duplicated rather than shared; loadScript/loadStyleSheet dedupe.
	const [isGoBrainLoaded, setIsGoBrainLoaded] = useState(false);
	const [isCssLoaded, setIsCssLoaded] = useState(false);

	const containerKey = useMemo(
		() => container?.getAttribute('data-qplayer-key') || `${container?.dataset?.qplayerId || generateUUID()}-reels`,
		[container]
	);

	// On mount rather than on first open, so opening a reel is instant.
	useEffect(() => {
		if (process.env.REACT_APP_GOBRAIN_URL) {
			if (container?.dataset?.qplayerPreview === 'true') {
				setIsGoBrainLoaded(true);
			} else {
				loadScript({ src: process.env.REACT_APP_GOBRAIN_URL, onload: () => setIsGoBrainLoaded(true) });
			}
		}
		if (process.env.PUBLIC_URL) {
			loadStyleSheet({ href: `${process.env.PUBLIC_URL}/qplayer.min.css`, onload: () => setIsCssLoaded(true) });
		}
		if (container?.dataset?.qplayerPreview !== 'true') {
			loadScript({ src: `https://play2.qbrick.com/md5/md5.js` });
		}
	}, [container]);

	const closeReels = useCallback(() => {
		setOpenRequest((current) => {
			// Focus back to whatever opened the feed, as <dialog> does.
			current?.trigger?.focus?.();
			return null;
		});
	}, []);

	// Both optional: no clip opens the first, no playlist uses the element's.
	const openReels = useCallback(
		(options = {}) => {
			const playlistId = options.playlistId || container?.dataset?.qplayerPlaylistId;
			if (!playlistId) {
				console.warn('[QPlayer] openReels needs a playlistId, in the call or on the element');
				return;
			}
			setOpenRequest({
				playlistId,
				mediaId: options.mediaId,
				trigger: options.trigger ?? null,
			});
		},
		[container]
	);

	// The element's own methods are the real API; window.QplayerReels is a façade over them.
	useEffect(() => {
		if (!container) {
			return undefined;
		}
		container.openReels = openReels;
		container.closeReels = closeReels;
		return () => {
			delete container.openReels;
			delete container.closeReels;
		};
	}, [container, openReels, closeReels]);

	// A card is anything marked data-qplayer-media-id, plus data-qplayer-id where a page holds
	// several feeds. Taking `trigger` here is why focus restore is never forgotten.
	useEffect(() => {
		if (!container) {
			return undefined;
		}
		const handleClick = (event) => {
			const card = event.target?.closest?.('[data-qplayer-media-id]');
			// <qplayer-embed data-qplayer-media-id> is an article player, not a card. Without this a
			// click on one opens the feed over it.
			if (!card || card.tagName.startsWith('QPLAYER-')) {
				return;
			}
			const wanted = card.dataset?.qplayerId;
			if (wanted) {
				if (wanted !== container.dataset?.qplayerId) {
					return;
				}
			} else {
				// Ambiguous: opening the wrong playlist is worse than doing nothing. First feed reports.
				const feeds = document.querySelectorAll('qplayer-reels-embed');
				if (feeds.length > 1) {
					if (feeds[0] === container) {
						console.warn(
							'[QPlayer] this card names no data-qplayer-id and the page has several feeds, so no feed opened'
						);
					}
					return;
				}
			}
			// The playlist is the feed's, never the card's.
			const playlistId = container.dataset?.qplayerPlaylistId;
			if (!playlistId) {
				console.warn('[QPlayer] this feed has no data-qplayer-playlist-id, so a card cannot open it');
				return;
			}
			// So a card that is a link does not navigate as well as opening; the href stays the
			// no-JS fallback and the address to share.
			event.preventDefault();
			openReels({ playlistId, mediaId: card.dataset?.qplayerMediaId, trigger: card });
		};
		document.addEventListener('click', handleClick);
		return () => document.removeEventListener('click', handleClick);
	}, [container, openReels]);

	// A deep link arriving cold. No trigger, because nothing was clicked, so closing falls back to
	// data-qplayer-gallery-url. The site still has to serve the address.
	useEffect(() => {
		const pattern = container?.dataset?.qplayerReelsUrl;
		if (!pattern) {
			return;
		}
		const clip = readReelsUrl(pattern);
		// A pattern carrying only {playlistId} still names a feed; it opens at its first clip.
		const playlistId = clip?.playlistId || container?.dataset?.qplayerPlaylistId;
		if (!clip || !playlistId) {
			// Every ordinary page load lands here. Silent.
			return;
		}
		openReels({ playlistId, mediaId: clip.mediaId });
		// Mount only: re-running would reopen a feed the viewer has closed.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [container, openReels]);

	const isOpen = !!openRequest;

	// The address bar, while the feed is open. Without a pattern the page owns its own.
	useReelsRoute({
		pattern: container?.dataset?.qplayerReelsUrl,
		galleryUrl: container?.dataset?.qplayerGalleryUrl,
		isOpen,
		playlistId: openRequest?.playlistId,
		mediaId: openRequest?.mediaId,
		qplayerId: container?.dataset?.qplayerId,
		// Back into an earlier clip: the same feed at a different one, trigger kept.
		onOpenClip: (clip) =>
			setOpenRequest((current) =>
				current ? { ...current, playlistId: clip.playlistId || current.playlistId, mediaId: clip.mediaId } : current
			),
		onClose: closeReels,
	});

	// Built while the feed is open and taken away with it, so a closed feed leaves nothing behind.
	const [hostEl, setHostEl] = useState(null);
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}
		const el = document.createElement('div');
		el.className = REELS_HOST_CLASS;
		document.body.appendChild(el);
		setHostEl(el);
		return () => {
			el.remove();
			setHostEl(null);
		};
	}, [isOpen]);

	// The page lock. Counted, so closing one feed does not unlock the page under another.
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}
		openFeedCount += 1;
		document.body.classList.add(REELS_BODY_CLASS);
		document.documentElement.classList.add(REELS_BODY_CLASS);
		return () => {
			openFeedCount -= 1;
			if (openFeedCount > 0) {
				return;
			}
			document.body.classList.remove(REELS_BODY_CLASS);
			document.documentElement.classList.remove(REELS_BODY_CLASS);
		};
	}, [isOpen]);

	// Escape, bound only while the feed is open.
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}
		const handleKeydown = (event) => {
			if (event.key === 'Escape') {
				closeReels();
			}
		};
		document.addEventListener('keydown', handleKeydown);
		return () => document.removeEventListener('keydown', handleKeydown);
	}, [isOpen, closeReels]);

	// The feed's close button is inside <App>, so it reports through the message channel.
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}
		const handleMessage = (event) => {
			const data = event.data;
			if (data?.identifier !== MessageHelper.MESSAGE_BASE_IDENTIFIERS.identifier) {
				return;
			}
			// Only this element's own player. A page can hold an article player too, and its close
			// must not take the feed down with it.
			if (data.qplayerId && container?.dataset?.qplayerId && data.qplayerId !== container.dataset.qplayerId) {
				return;
			}
			if (data.type === MessageHelper.MESSAGE_TYPES.closePlayer) {
				closeReels();
			}
		};
		window.addEventListener('message', handleMessage);
		return () => window.removeEventListener('message', handleMessage);
	}, [isOpen, closeReels, container]);

	if (!isOpen || !hostEl || !isGoBrainLoaded || !isCssLoaded) {
		return null;
	}

	// Portalled to <body>: an ancestor with overflow, a transform or its own stacking context would
	// trap a fixed overlay where the publisher happened to put the element.
	return createPortal(
		<StrictMode>
			<PlayerProvider>
				<ConfigurationProvider>
					<ChatLoginProvider>
						<PresenterProvider>
							<ShoppingProvider>
								<RestrictionProvider>
									<I18nextProvider i18n={i18n}>
										<App
											// Config off the custom element; call arguments below beat it.
											{...container?.dataset}
											qplayerPlaylistId={openRequest.playlistId}
											qplayerPlaylistInitMediaId={openRequest.mediaId || ''}
											// Always a softbox; there is no inline reels mode.
											qplayerReels="on"
											qplayerInline="off"
											// The declared pattern doubles as the share template, made
											// absolute. An explicit data-qplayer-share-url still wins.
											qplayerShareUrl={
												container?.dataset?.qplayerShareUrl ||
												reelsShareTemplate(container?.dataset?.qplayerReelsUrl)
											}
											// The host, not the custom element. See REELS_HOST_CLASS.
											container={hostEl}
											containerKey={containerKey}
										/>
									</I18nextProvider>
								</RestrictionProvider>
							</ShoppingProvider>
						</PresenterProvider>
					</ChatLoginProvider>
				</ConfigurationProvider>
			</PlayerProvider>
		</StrictMode>,
		hostEl
	);
};

export default QplayerAppReels;
